import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { AddIcon, ShareIcon } from 'tdesign-icons-react';
import { Button, Form } from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import { debounce, maxBy } from 'lodash';
import PermWrapper from '@/components/PermWrapper';
import {
  doSort,
  fetchAllClusterEnvs,
  fetchBasicAsync,
  fetchClusterList,
  resetAllList,
} from '@/slices/clusterSlice';
import {
  fetchAllBKVersions,
  fetchAllDeployEnvs,
  fetchAllDeployVersions,
  fetchAllPulsarVersions,
  fetchAllZKVersions,
  fetchClusterUsage,
} from '@/slices/dictSlice';
import CreateClusterModal from './CreateClusterModal';
import FilterForm from './FilterForm';
import LinkClusterModal from './LinkClusterModal';
import ListContainer from './ListContainer';
import EnvSelect from './EnvSelect';

const initialData = {
  deployEnv: '',
  version: '',
  sortKey: 'clusterTypeOrder',
  sortType: 'desc',
};

function ClusterList({
  fetchBasicAsync,
  fetchAllDeployEnvs,
  fetchAllClusterEnvs,
  fetchAllPulsarVersions,
  fetchClusterUsage,
  fetchAllBKVersions,
  fetchAllDeployVersions,
  fetchAllZKVersions,
  fetchClusterList,
  resetAllList,
  doSort,
}) {
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [linkModalVisible, setLinkModalVisible] = useState(false);
  const [currentFilter, setCurrentFilter] = useState(initialData);
  const [form] = Form.useForm();

  const loadData = useMemoizedFn(async (filter) => {
    // 先清空addon缓存
    resetAllList();

    const params = {
      ...currentFilter,
      ...filter,
    };
    const { sortKey, sortType, ...others } = params;
    await fetchClusterList(others);
    doSort({ sortKey, sortType });
    setCurrentFilter(params);
  });

  // 初始化
  const doInitDataLoad = useMemoizedFn(async () => {
    const { payload: clusterEnvs } = await fetchAllClusterEnvs();
    const defaultEnv = maxBy(clusterEnvs, 'clusterNum');
    const initValues = { ...initialData, clusterEnv: defaultEnv.value };
    form.setFieldsValue(initValues);
    loadData(initValues);
  });

  useEffect(() => {
    fetchBasicAsync();
    // 以下老字典初始化
    fetchAllDeployEnvs();
    fetchAllBKVersions();
    fetchClusterUsage();
    fetchAllZKVersions();
    fetchAllDeployVersions();
    fetchAllPulsarVersions();

    doInitDataLoad();
  }, [
    fetchBasicAsync,
    fetchAllDeployEnvs,
    fetchAllPulsarVersions,
    fetchClusterUsage,
    fetchAllBKVersions,
    fetchAllDeployVersions,
    fetchAllZKVersions,
    doInitDataLoad,
  ]);

  const handleCreateOpen = () => {
    setCreateModalVisible(true);
  };

  const handleCreateClose = () => {
    setCreateModalVisible(false);
  };

  const handleLinkOpen = () => {
    setLinkModalVisible(true);
  };

  const handleLinkClose = (doRefresh) => {
    setLinkModalVisible(false);
    if (doRefresh) {
      loadData();
    }
  };

  const filterChange = debounce((changed, allValues) => {
    loadData(allValues);
  }, 100);

  return (
    <Root className='fadeIn'>
      <Form form={form} onValuesChange={filterChange} colon labelAlign='top'>
        <TitleLine>
          <Title>
            集群列表
            <EnvSelect />
          </Title>
          <ButtonGroups>
            <PermWrapper resource='pulsar:cluster:cluster_list:join'>
              <Button
                onClick={handleLinkOpen}
                shape='round'
                variant='outline'
                icon={<ShareIcon style={{ transform: 'rotate(-90deg)' }} />}
              >
                接入集群
              </Button>
            </PermWrapper>
            <PermWrapper resource='pulsar:cluster:cluster_list:create'>
              <Button onClick={handleCreateOpen} shape='round' variant='base' icon={<AddIcon />}>
                创建集群
              </Button>
            </PermWrapper>
          </ButtonGroups>
        </TitleLine>
        <Body>
          <FilterForm />
          <ListContainer />
          <CreateClusterModal visible={createModalVisible} handleClose={handleCreateClose} />
          <LinkClusterModal visible={linkModalVisible} handleClose={handleLinkClose} />
        </Body>
      </Form>
    </Root>
  );
}

export default connect(null, {
  fetchBasicAsync,
  fetchAllDeployEnvs,
  fetchAllClusterEnvs,
  fetchAllBKVersions,
  fetchClusterUsage,
  fetchAllZKVersions,
  fetchAllDeployVersions,
  fetchAllPulsarVersions,
  fetchClusterList,
  resetAllList,
  doSort,
})(ClusterList);

const Root = styled.div`
  padding: 20px 80px;
`;

const TitleLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.div`
  font-weight: 500;
  font-size: 22px;
  line-height: 31px;
  color: #0d111a;
  display: flex;
  align-items: center;
`;

const ButtonGroups = styled.div`
  .t-button {
    height: 40px;
    margin-left: 12px;
    padding-left: 22px;
    padding-right: 22px;
  }
  .t-button--variant-outline {
    box-shadow: 0px 1px 2px -1px rgba(0, 0, 0, 0.1), 0px 1px 3px rgba(0, 0, 0, 0.1);
    border: none;
  }
  .t-button--variant-base {
    font-weight: bolder;
  }
`;

const Body = styled.div`
  display: flex;
  margin-top: 20px;
  align-items: flex-start;
`;
